import unique from 'lodash/uniq';
import { createSelector } from 'reselect';
import quicksort from 'quicksort-js';

import { bugsnagError } from 'services/bugsnag';

import {
  getActiveDesign,
  getIndexedProducts,
  getIndexedRevisions,
  getRenders
} from 'redux/Studio';

import Filter from './Filter';
import SearchBase from './SearchBase';

/**
 * Products In Design search implementation.
 *
 * This search is restricted to products within a
 * single design. Because the number of products is
 * limited, we currently perform all filtering and
 * sorting client side.
 *
 * The search accounts for the current design revison.
 * Only products in the current design revision are returned
 * in the search results.
 */
export default class ProductsInRoom extends SearchBase {
  /** Creates a new ProductsInRoom copy with optional overrides. */
  makeCopy (overrides) {
    return new ProductsInRoom({ ...this.fields, ...overrides });
  }

  /**
   * This search is syncronous.
   */
  isAsync () {
    return false;
  }

  /**
   * Run query
   * This is a no-op for ProductsInDesign search.
   */
  runQuery () {
    return null;
  }

  /**
   * For ProductsInDesign search, we want to
   * run the search query on the selector side
   * so search results pick up new Products as
   * they are added to our internal Product Database.
   *
   * The selector is memoized so the new result will
   * only be compted when something changes.
   *
   * @param {Object} state - The redux state object.
   * @returns {Product[]} products - Matching produts
   */
  products (state) {
    return executeSearch(state, this.enabledFilters());
  }

  /**
   * Returns the dynamic filters available based
   * on the products in the current design.
   *
   * The selector is memoized so that new filters
   * will only be computed when something changes.
   *
   * @param {Object} state - The redux state object.
   * @returns {Filter[]} filters
   */
  availableFilters ({ state }) {
    return getAvailableFilters(state);
  }

  /**
   * Returns the dynamic sorts available based
   * on the last search execution.
   * @param {Object} state - The redux state object.
   * @returns {Filter[]} filters
   */
  availableSorts (state) {
    return {};
  }
}

/**
 * Returns the products matching the search query.
 * Memozied for efficiency.
 */
const executeSearch = createSelector(
  (state) => getAllProductsInDesignSortedByPrice(state),
  (state) => getAvailableFilters(state),
  (state, enabledFilters) => enabledFilters,
  (productsInDesign, filters, enabledFilters) => {
    // Apply Filters
    // The result here is an intersection.
    let result = productsInDesign;
    for (let filter of filters) {
      const enabledValues = enabledFilters[filter.label()] || [];
      result = filter.apply(result, enabledValues);
    }

    return result;
  }
);

/**
 * Returns all available filters and values.
 * These are dynamic based on the products
 * in the design.
 */
const getAvailableFilters = createSelector(
  (state) => getAllProductsInDesignSortedByPrice(state),
  (products) => {
    let filters = [];

    // =============
    // Brands Filter
    // =============
    let brands = products.map(product => product.retailer());
    brands = [...new Set(brands)];
    brands = quicksort(brands, (a, b) => ('' + a).localeCompare(b));
    if (brands) {
      filters.push(new Filter({
        label: 'Brand',
        values: brands,
        onApply (products, enabledValues) {
          if (enabledValues.length === 0) {
            return products; // nothing enabled is the same as all enabled.
          }

          const result = products.filter(product => enabledValues.includes(product.retailer()));
          return result;
        }
      }));
    }

    // =============
    // Price Filter
    // Buckets: Under $100, $100-$500, 5$00-$1000, Above $1000
    // =============
    const prices = products.map(product => product.currentPrice());

    let values = [];
    if (prices.some(price => price < 100)) {
      values.push('Under $100');
    }

    if (prices.some(price => price >= 100 && price < 500)) {
      values.push('$100 - $500');
    }

    if (prices.some(price => price >= 500 && price < 1000)) {
      values.push('$500 - $1000');
    }

    if (prices.some(price => price >= 1000)) {
      values.push('Above $1000');
    }

    filters.push(new Filter({
      label: 'Price',
      values,
      onApply (products, enabledValues) {
        if (enabledValues.length === 0) {
          return products; // nothing enabled is the same as all enabled.
        }

        const lt100 = enabledValues.includes('Under $100');
        const gte100lt500 = enabledValues.includes('$100 - $500');
        const gte500lt1000 = enabledValues.includes('$500 - $1000');
        const gte1000 = enabledValues.includes('Above $1000');

        const result = products.filter(product => {
          const price = product.currentPrice();
          return (
            (lt100 && price < 100) ||
            (gte100lt500 && price >= 100 && price < 500) ||
            (gte500lt1000 && price >= 500 && price < 1000) ||
            (gte1000 && price >= 1000)
          );
        });

        return result;
      }
    }));

    // =============
    // Color Filter
    // =============
    let colors = products.map(product => product.color()).filter(color => !!color);
    colors = [...new Set(colors)];
    colors = quicksort(colors, (a, b) => ('' + a).localeCompare(b));
    if (colors) {
      filters.push(new Filter({
        label: 'Color',
        values: colors,
        onApply (products, enabledValues) {
          if (enabledValues.length === 0) {
            return products; // nothing enabled is the same as all enabled.
          }

          const result = products.filter(product => enabledValues.includes(product.color()));
          return result;
        }
      }));
    }

    // ==============
    // On Sale Filter
    // ==============
    const oneProductOnSale = products.some(product => product.isDiscounted());
    if (oneProductOnSale) {
      filters.push(new Filter({
        label: 'On Sale',
        values: ['On Sale', 'Regular Price'],
        onApply (products, enabledValues) {
          if (enabledValues.length === 0) {
            return products; // nothing enabled is the same as all enabled.
          }

          const onSale = enabledValues.includes('On Sale');
          const regularPrice = enabledValues.includes('Regular Price');

          const result = products.filter(product => {
            return (
              (onSale && product.isDiscounted()) ||
              (regularPrice && !product.isDiscounted())
            );
          });

          return result;
        }
      }));
    }

    return filters;
  }
);

/**
 * Returns all the products in the active design
 * sorted from most expensive to least expensive.
 * Memoized for efficiency.
 */
const getAllProductsInDesignSortedByPrice = createSelector(
  (state) => getAllProductsInDesign(state),
  (products) => {
    // Sort the products from most expensive to least expensive.
    const sorted = quicksort(products, (a, b) => {
      return b.currentPrice() - a.currentPrice();
    });

    return sorted;
  }
);

/**
 * Returns all the products in the active design revision.
 * Memoized for efficiency.
 */
const getAllProductsInDesign = createSelector(
  (state) => getActiveDesign({ state }),
  (state) => getIndexedRevisions(state),
  (state) => getIndexedProducts(state),
  (state) => getRenders(state),
  (design, revisions, allProducts, allRevisionRenders) => {
    if (!design) { return []; }

    // Start with products in design.
    let productIds = design.productIds();

    // Walk back through revisions until we find the first non-liveswap revision.
    // From that revision start walking forward through revisions.
    //
    // While walking we need to search through revision renders until we find the
    // matching hotspot UUID so we can get at the product ID that was replaced out. This
    // product ID should then be dropped from Products In Design. What makes this tricky is that
    // we need to search through the prior revision renders to find the product ID that was
    // dropped in the current revision.
    //
    // Note:
    // For Bessie and Showroom revisions we expect that the products in design list has
    // been updated correctly from server response. This is why we find the first non
    // liveswap revision to serve as our base revision and we expect that products in
    // design is accurate w.r.t. that base revision.
    let productIdsToRemove = [];
    try {
      const revisionHistory = design.revisionHistory();

      let baseRevisionIndex = revisionHistory.length - 1;
      for (let i = 0; i < revisionHistory.length; i++) {
        const revisionUuid = revisionHistory[i];
        const revision = revisions[revisionUuid];
        if (!revision.isFromStudio()) {
          baseRevisionIndex = i;
          break;
        }
      }

      for (let i = baseRevisionIndex; i >= 0; i--) {
        const revisionUuid = revisionHistory[i];
        const revision = revisions[revisionUuid];

        // We actually need to search the renders 1 revision back
        // in order to find the product IDs that were dropped.
        for (let instanceUuid of revision.replacementHotspotUuids()) {
          if (i === (revisionHistory.length - 1)) {
            for (let render of design.renders()) {
              const hotspots = render.hotspots || [];
              const hotspot = hotspots.find(hs => hs.instanceId === instanceUuid || hs.instance_id === instanceUuid);
              if (hotspot) {
                productIdsToRemove.push(hotspot.productId || hotspot.product_id);
                break;
              }
            }
          } else {
            const searchRevision = revisions[revisionHistory[i + 1]];
            for (let renderUid of Object.values(searchRevision.renders())) {
              const render = allRevisionRenders[renderUid];
              const hotspots = render.draftVariant().hotspots();
              const hotspot = hotspots.find(hs => hs.instanceId === instanceUuid);
              if (hotspot) {
                productIdsToRemove.push(hotspot.productId);
                break;
              }
            }
          }
        }

        // The same product could be toggled in and out of revisions.
        // Here, we check if the product was added in this revision, and
        // if so we remove it from the products to remove list.
        for (let productId of revision.replacementProductIds()) {
          productIdsToRemove = productIdsToRemove.filter(id => id !== productId);
        }
      }
    } catch (error) {
      bugsnagError(error);
    }

    // Remove that products that were dropped in a liveswap revision.
    productIds = productIds.filter(id => !productIdsToRemove.includes(id));

    // Generate unique product ID list.
    let uniqueProductIds = unique(productIds);

    // Map product IDs to product instances.
    const products = uniqueProductIds
      .map(id => allProducts[id])
      .filter(product => {
        return product && (
          // Exclude products with no price.
          product.currentPrice() > 0 || product.regularPrice() > 0 || product.isMaterial()
        );
      });

    return products || [];
  }
);
